import { useEffect, useRef } from "react";

interface ShortcutHandlers {
  readonly back: () => void;
  readonly zoomOut: () => void;
  readonly sibling: (direction: "prev" | "next") => void;
  readonly search: () => void;
  readonly help: () => void;
}

function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}

function somethingOpen(): boolean {
  return document.querySelector("dialog[open], [data-menu-aberto]") !== null;
}

export function useShortcuts(handlers: ShortcutHandlers): void {
  const latest = useRef(handlers);
  latest.current = handlers;
  useEffect(() => {
    const pressed = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || typing(event.target) || somethingOpen()) return;
      const act = actionOf(event.key);
      if (!act) return;
      event.preventDefault();
      act(latest.current);
    };
    document.addEventListener("keydown", pressed);
    return () => document.removeEventListener("keydown", pressed);
  }, []);
}

function actionOf(key: string): ((handlers: ShortcutHandlers) => void) | undefined {
  switch (key) {
    case "Escape":
      return (h) => h.back();
    case "-":
      return (h) => h.zoomOut();
    case "ArrowLeft":
      return (h) => h.sibling("prev");
    case "ArrowRight":
      return (h) => h.sibling("next");
    case "f":
    case "F":
      return (h) => h.search();
    case "?":
      return (h) => h.help();
    default:
      return /^[areu]$/i.test(key) ? () => press(key.toLowerCase()) : undefined;
  }
}

function press(key: string): void {
  const button = document.querySelector(`[data-nivel]:not([inert]) [data-atalho="${key}"], [role="status"] [data-atalho="${key}"]`);
  if (button instanceof HTMLButtonElement && !button.disabled) button.click();
}
